/* istanbul ignore file */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef, Ref, forwardRef, useImperativeHandle } from 'react';
import { Editor as TinyMceReact } from '@tinymce/tinymce-react';
import { makeStyles } from 'tss-react/mui';
import { InputLabel } from '@mui/material';
import { CircularProgress } from '../circular-progress/circular-progress';
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog';
import { ColorPalette } from '../config/color-palette';
import { TypographySettings } from '../config/typography-settings';
import { IEditorProps } from './editor-props';
import { config, tinyMceI18n } from './tinymce';

import 'tinymce/tinymce';
import './tinymce/dependencies';
import 'tinymce/themes/silver';
import './editor.scss';
import './styling/company-editor-custom-tinymce-content.scss';

export interface IEditorRef {
  removeDraft: () => void;
}

const useStyles = makeStyles()(() =>
  ({
    editorBoxError: {
      borderColor: `${ColorPalette.HotAurora}!important`,
    },
    editorMessageError: {
      color: ColorPalette.HotAurora,
      marginLeft: 14,
      marginTop: 3,
      fontSize: TypographySettings.DefaultFontSize,
    },
    label: {
      color: ColorPalette.InputLabel,
      fontSize: TypographySettings.DefaultFontSize,
      marginBottom: 8,
      transform: 'none',
      position: 'static',
    },
  })
);

export const Editor = forwardRef((props: IEditorProps, ref: Ref<IEditorRef>) => {
  const {
    className = '',
    error,
    errorText,
    disabled,
    toolbar1,
    customInit,
    onChange = () => {},
    autoSavePrefix,
    onRestoreDraft = () => {},
    value,
    resizable,
    onBlur,
    label,
    selectorPrefix = '',
    autoFocus = false,
  } = props;
  const [draftIsRestoring, setDraftIsRestoring] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorInstance = useRef<any>(null);

  const customizedRefValue: IEditorRef = {
    removeDraft,
  };

  const { classes } = useStyles();

  useImperativeHandle(ref, () => customizedRefValue, [editorInstance.current]);

  useEffect(() => {
    (window as any).tinymce.util.I18n.add('da', tinyMceI18n);
  }, []);

  function registerOnClickHandlerForInputs(editor: any) {
    const editorBody = editor.getBody() as HTMLBodyElement;
    const radioElements = editorBody.querySelectorAll('input[type=radio]');
    radioElements.forEach(element => {
      element.addEventListener('change', event => {
        const target = event.target as HTMLInputElement;
        const targetName = target.name;
        const parentForm = target.closest('form');
        if (parentForm == null) return;
        // Remove "checked" from all radiobuttons in the named group
        const radioButtonsInForm = parentForm.children as HTMLCollectionOf<HTMLInputElement>;
        for (let j = 0; j < radioButtonsInForm.length; j += 1) {
          if (radioButtonsInForm[j].name === targetName) {
            radioButtonsInForm[j].removeAttribute('checked');
          }
        }
        target.setAttribute('checked', 'checked');
        target.checked = true;
      });
    });
    const checkboxElements = editorBody.querySelectorAll('input[type=checkbox]');
    checkboxElements.forEach(element => {
      element.addEventListener('change', event => {
        const target = event.target as HTMLInputElement;
        if (target.checked) {
          target.setAttribute('checked', 'checked');
        } else {
          target.removeAttribute('checked');
        }
      });
    });
  }

  function makeInputsEditable(editor: any) {
    try {
      const content = editor.getBody() as HTMLBodyElement;
      const found = content.querySelectorAll('input[type=checkbox], input[type=radio]');
      // Filter out elements with "data-was-processed"
      const filteredFound: Element[] = [];
      found.forEach(element => {
        if (element.getAttribute('data-was-processed') === 'true') {
          filteredFound.push(element);
        }
      });
      filteredFound.forEach(element => {
        element.classList.remove('mceNonEditable');
        element.classList.add('mceEditable');
        // eslint-disable-next-line no-param-reassign
        (element as any).contenteditable = true;
        element.setAttribute('data-was-processed', 'true');
        const { parentElement } = element;
        if (parentElement != null) {
          parentElement.classList.add('mceNonEditable');
          (parentElement as any).contenteditable = false;
        }
      });
      if (filteredFound.length > 0) {
        content.prepend(document.createElement('div'));
      }
    } catch (err: any) {
      console.log(err);
    }
  }

  function onRestoreDraftCallBack(editor: any, event: any) {
    if (onRestoreDraft) {
      onRestoreDraft(event);
    }

    setTimeout(() => {
      registerOnClickHandlerForInputs(editor);
      setIsOpen(false);
      setDraftIsRestoring(false);
    }, 1500);
    registerOnClickHandlerForInputs(editor);
  }

  const onInitCallBack = (editor: any) => {
    // if (editor.plugins.autosave.hasDraft()) {
    //   const dialogText = getText(new Date(getDraftTime(editor)));
    //   setDialogContent(dialogText);
    //   setIsOpen(true);
    // }
    registerOnClickHandlerForInputs(editor);

    if (autoFocus) {
      setTimeout(() => {
        editor.focus();
      }, 100);
    } else {
      setTimeout(() => {
        if (editor.hasFocus()) {
          editor.getBody()?.blur();
        }
      }, 200);
    }
  };

  const handleDialogConfirmClick = () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    editorInstance!.current!.editor!.plugins!.autosave!.restoreDraft();
    setDraftIsRestoring(true);
  };

  const handleDialogCancelClick = () => {
    removeDraft();
    setIsOpen(false);
  };

  function removeDraft() {
    editorInstance!.current!.editor!.plugins!.autosave!.removeDraft();
  }

  const toolbarContainerId = `${selectorPrefix}customtinytoolbar`;
  const contentContainerId = `${selectorPrefix}customtinycontent`;
  return (
    <>
      {label && (
        <InputLabel shrink className={classes.label} error={error === true}>
          {label}
        </InputLabel>
      )}
      <TinyMceReact
        ref={editorInstance}
        onBlur={onBlur}
        disabled={disabled}
        init={{
          ...config,
          ...customInit,
          resize: resizable,
          autosave_prefix: autoSavePrefix,
          inline: true,
          skin: 'oxide',
          skin_url: '/tinymce/skins/ui/oxide',
          language_url: '/tinymce/languages/da.js',
          fixed_toolbar_container: `#${toolbarContainerId}`,
          selector: `#${contentContainerId}`,
          setup: (editor: any) => {
            editor.on('init', () => {
              onInitCallBack(editor);
            });
            editor.on('beforeSetContent', () => {
              makeInputsEditable(editor);
            });
            editor.on('restoreDraft', (e: any) => onRestoreDraftCallBack(editor, e));
          },
          toolbar1: toolbar1 || config.toolbar1,
        }}
        value={value}
        onEditorChange={onChange}
      />
      <div id={toolbarContainerId} className="custom-toolbar" />
      <div
        id={contentContainerId}
        className={`master-editor custom-tinymce ${className} ${error === true ? classes.editorBoxError : ''}`}
        style={{ maxHeight: 800, overflowY: 'auto' }}
      />
      {error === true && <div className={classes.editorMessageError}>{errorText}</div>}
      <ConfirmDialog
        type="question"
        open={isOpen}
        title="Gendan sikkerhedskopi"
        cancelButtonText="Nej, slet sikkerhedskopi"
        confirmButtonText="Ja, gendan brev"
        onConfirmClick={handleDialogConfirmClick}
        onCancelClick={handleDialogCancelClick}
      >
        {dialogContent.split('\n').map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i}>
            {line}
            <br />
          </span>
        ))}
        {draftIsRestoring && <CircularProgress />}
      </ConfirmDialog>
    </>
  );
});
